import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { SIZES } from './CreativeCanvas';
import {
  Loader2, Wand2, Rocket, Palette, CalendarDays, LayoutGrid, Clock, Sparkles, Link2, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const PLATFORMS = [
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'bluesky', label: 'Bluesky' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'youtube', label: 'YouTube' },
];
const TONES = ['Professional', 'Friendly', 'Bold', 'Playful', 'Inspirational', 'Luxury'];

const isoDate = (d) => d.toISOString().slice(0, 10);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * BrandedCampaignBuilder — AI Creative Studio "Branded campaign" mode.
 *
 * Auto-creates a SCHEDULED campaign where every AI image is brand-framed
 * (Spotlight overlay: logo + brand colors + the post's own title). Reuses the
 * existing campaign engine end-to-end (no new backend):
 *   1) POST /campaigns/preview-async  → poll /campaigns/preview-status
 *   2) POST /campaigns/launch-full    → save + schedule + queue brand-framed images
 */
export const BrandedCampaignBuilder = ({ kits = [] }) => {
  const { api } = useAuth();
  const navigate = useNavigate();

  const today = useMemo(() => new Date(), []);
  const weekOut = useMemo(() => new Date(Date.now() + 6 * 86400000), []);

  const [brief, setBrief] = useState('');
  const [audience, setAudience] = useState('');
  const [tone, setTone] = useState('Professional');
  const [platforms, setPlatforms] = useState(['linkedin']);
  const [startDate, setStartDate] = useState(isoDate(today));
  const [endDate, setEndDate] = useState(isoDate(weekOut));
  const [postsPerDay, setPostsPerDay] = useState(1);
  const [kitId, setKitId] = useState('');
  const [size, setSize] = useState('1:1');

  const [phase, setPhase] = useState('form'); // form | previewing | preview | scheduling
  const [preview, setPreview] = useState(null);
  const [targets, setTargets] = useState([]);
  const cancelRef = useRef(false);
  const [selAccounts, setSelAccounts] = useState({}); // platform -> [keys]

  useEffect(() => {
    if (kits[0]) setKitId(kits[0].id);
    api.get('/connections/targets').then((r) => setTargets(r.data?.targets || [])).catch(() => {});
    return () => { cancelRef.current = true; };
  }, [kits, api]);

  const kit = kits.find((k) => k.id === kitId) || null;
  const keyOf = (t) => `${t.platform}::${t.account_id}`;

  const accountsByPlatform = useMemo(() => {
    const m = {};
    for (const t of targets) { (m[t.platform] = m[t.platform] || []).push(t); }
    return m;
  }, [targets]);

  // Keep a valid default account selection per chosen platform.
  useEffect(() => {
    setSelAccounts((cur) => {
      const next = {};
      for (const p of platforms) {
        const accts = accountsByPlatform[p] || [];
        if (accts.length === 0) continue;
        const kept = (cur[p] || []).filter((k) => accts.some((t) => keyOf(t) === k));
        next[p] = kept.length ? kept : [keyOf(accts[0])];
      }
      return next;
    });
  }, [platforms, accountsByPlatform]);

  const togglePlatform = (id) => setPlatforms((cur) => (
    cur.includes(id) ? cur.filter((p) => p !== id) : [...cur, id]
  ));

  const toggleAccount = (platform, key) => setSelAccounts((cur) => {
    const list = cur[platform] || [];
    const next = list.includes(key) ? list.filter((k) => k !== key) : [...list, key];
    return { ...cur, [platform]: next };
  });

  const generatePreview = async () => {
    if (brief.trim().length < 10) { toast.error('Add a longer campaign brief (10+ chars)'); return; }
    if (platforms.length === 0) { toast.error('Pick at least one platform'); return; }
    if (endDate < startDate) { toast.error('End date must be on/after start date'); return; }
    cancelRef.current = false;
    setPhase('previewing');
    setPreview(null);
    try {
      const start = await api.post('/campaigns/preview-async', {
        prompt: brief, platforms, start_date: startDate, end_date: endDate,
        posts_per_day: postsPerDay, tone, audience: audience || null,
      });
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error('No job id');
      // Poll until complete (max ~90s).
      for (let i = 0; i < 45; i += 1) {
        if (cancelRef.current) return;
        await sleep(2000);
        const st = await api.get(`/campaigns/preview-status/${jobId}`);
        const d = st.data || {};
        if (d.status === 'complete') {
          setPreview(d.result);
          setPhase('preview');
          return;
        }
        if (d.status === 'failed') {
          toast.error(d.error || 'Preview failed — try again');
          setPhase('form');
          return;
        }
      }
      toast.error('Preview timed out — try a shorter window');
      setPhase('form');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate campaign preview');
      setPhase('form');
    }
  };

  const scheduleCampaign = async () => {
    if (!preview?.items?.length) return;
    setPhase('scheduling');
    try {
      // Use the user's per-platform account selection (defaults to first).
      const targets_by_platform = {};
      for (const p of platforms) {
        const keys = selAccounts[p] || [];
        const objs = keys
          .map((k) => (accountsByPlatform[p] || []).find((t) => keyOf(t) === k))
          .filter(Boolean);
        if (objs.length) targets_by_platform[p] = objs;
      }
      const r = await api.post('/campaigns/launch-full', {
        items: preview.items,
        prompt: brief, platforms, tone, audience: audience || null,
        start_date: startDate, end_date: endDate, posts_per_day: postsPerDay,
        goal: null, blueprint: null,
        targets_by_platform,
        generate_images: true,
        image_model: 'nano-banana',
        brand_frame: !!kitId,
        brand_kit_id: kitId || null,
        creative_size: size,
        draft_emails: false,
      });
      const d = r.data || {};
      toast.success(`Branded campaign scheduled — ${d.total_scheduled || d.posts_saved} posts, images generating`);
      navigate(`/posts?campaign=${d.campaign_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not schedule campaign');
      setPhase('preview');
    }
  };

  const busy = phase === 'previewing' || phase === 'scheduling';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="branded-campaign-builder">
      {/* Inputs */}
      <div className="space-y-4">
        <Card><CardContent className="p-4 space-y-4">
          <div className="space-y-1">
            <Label className="text-xs">Campaign brief</Label>
            <Textarea rows={3} value={brief} onChange={(e) => setBrief(e.target.value)}
              placeholder="e.g. A 1-week push promoting our new cold-brew line to busy professionals"
              maxLength={2000} data-testid="campaign-brief" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Audience (optional)</Label>
            <Input value={audience} onChange={(e) => setAudience(e.target.value)}
              placeholder="e.g. urban coffee lovers, 25-40" data-testid="campaign-audience" />
          </div>
          <div>
            <Label className="text-xs">Platforms</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {PLATFORMS.map((p) => {
                const on = platforms.includes(p.id);
                return (
                  <button key={p.id} onClick={() => togglePlatform(p.id)} data-testid={`campaign-platform-${p.id}`}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'}`}>
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>
          {platforms.length > 0 && (
            <div data-testid="campaign-accounts">
              <Label className="text-xs flex items-center gap-1"><Link2 className="w-3 h-3" /> Publish to (account)</Label>
              <div className="space-y-2.5 mt-1.5">
                {platforms.map((p) => {
                  const accts = accountsByPlatform[p] || [];
                  const label = PLATFORMS.find((x) => x.id === p)?.label || p;
                  return (
                    <div key={p}>
                      <p className="text-[11px] font-medium text-zinc-500 mb-1">{label}</p>
                      {accts.length === 0 ? (
                        <p className="text-[11px] text-amber-600">
                          No connected {label} account —{' '}
                          <button type="button" onClick={() => navigate('/connections')} className="underline">connect one</button>.
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {accts.map((t) => {
                            const k = keyOf(t);
                            const on = (selAccounts[p] || []).includes(k);
                            return (
                              <button key={k} type="button" onClick={() => toggleAccount(p, k)}
                                data-testid={`campaign-account-${p}-${t.account_id}`}
                                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors flex items-center gap-1 ${on ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'}`}>
                                {on && <CheckCircle2 className="w-3 h-3" />}
                                <span className="truncate max-w-[140px]">{t.account_name || t.connection_account_name || 'Account'}</span>
                                {t.kind === 'org' && <span className="text-[9px] text-zinc-400">org</span>}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Start</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="campaign-start" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1"><CalendarDays className="w-3 h-3" /> End</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="campaign-end" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Posts / day / platform</Label>
              <Select value={String(postsPerDay)} onValueChange={(v) => setPostsPerDay(Number(v))}>
                <SelectTrigger data-testid="campaign-ppd"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger data-testid="campaign-tone"><SelectValue /></SelectTrigger>
                <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </CardContent></Card>

        <Card><CardContent className="p-4 space-y-4">
          <p className="text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
            <Palette className="w-3.5 h-3.5 text-indigo-600" /> Branding (Spotlight overlay)
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Brand kit</Label>
              <Select value={kitId || 'none'} onValueChange={(v) => setKitId(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="campaign-brandkit"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No branding (plain photo)</SelectItem>
                  {kits.map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1"><LayoutGrid className="w-3 h-3" /> Size</Label>
              <Select value={size} onValueChange={setSize}>
                <SelectTrigger data-testid="campaign-size"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(SIZES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {platforms.includes('instagram') && size === '9:16' && (
            <div className="flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="campaign-ig-ratio-warning">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>9:16 is for Stories/Reels — Instagram <strong>feed</strong> posts will crop this and may clip your overlay text. Use <strong>1:1</strong> or <strong>4:5</strong> for feed.</span>
            </div>
          )}
          {kits.length === 0 && (
            <button onClick={() => navigate('/brand-kit')} className="text-xs text-indigo-600 hover:underline flex items-center gap-1" data-testid="campaign-create-kit-link">
              <Palette className="w-3.5 h-3.5" /> Create a brand kit to overlay your logo & colors on every image
            </button>
          )}
          <p className="text-[11px] text-zinc-400 leading-relaxed">
            Each post's AI-generated title is overlaid on its own image, with your logo, brand colors,
            gradient and CTA pill. Captions & hashtags are written automatically.
          </p>
          <Button onClick={generatePreview} disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="generate-campaign-btn">
            {phase === 'previewing' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
            {phase === 'previewing' ? 'Planning campaign…' : 'Generate campaign preview'}
          </Button>
        </CardContent></Card>
      </div>

      {/* Preview / result */}
      <div className="space-y-4">
        <Card><CardContent className="p-4">
          {phase === 'previewing' && (
            <div className="flex flex-col items-center justify-center py-16 text-center gap-3" data-testid="campaign-previewing">
              <Loader2 className="w-7 h-7 animate-spin text-indigo-600" />
              <p className="text-sm text-zinc-600">Planning your campaign across {platforms.length} platform{platforms.length === 1 ? '' : 's'}…</p>
              <p className="text-[11px] text-zinc-400">This usually takes 10–30 seconds.</p>
            </div>
          )}

          {phase === 'form' && (
            <div className="flex flex-col items-center justify-center py-16 text-center gap-3 text-zinc-400" data-testid="campaign-empty">
              <Sparkles className="w-8 h-8 text-indigo-200" />
              <p className="text-sm">Your branded campaign preview will appear here.</p>
            </div>
          )}

          {(phase === 'preview' || phase === 'scheduling') && preview && (
            <div className="space-y-4" data-testid="campaign-preview-result">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">{preview.total} posts</Badge>
                <Badge variant="outline">{preview.days} day{preview.days === 1 ? '' : 's'}</Badge>
                {kit && <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200"><Palette className="w-3 h-3 mr-1" /> {kit.name}</Badge>}
                <Badge variant="outline">{SIZES[size]?.label}</Badge>
              </div>
              <div className="max-h-[420px] overflow-y-auto divide-y divide-zinc-100 rounded-md border border-zinc-200">
                {preview.items.map((it, i) => (
                  <div key={i} className="p-3 text-xs" data-testid={`campaign-item-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-zinc-800 truncate">{it.post_title}</span>
                      <span className="capitalize text-[10px] text-zinc-500 shrink-0">{it.platform}</span>
                    </div>
                    <p className="text-zinc-500 mt-1 line-clamp-2">{it.caption}</p>
                    <p className="text-[10px] text-zinc-400 mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {new Date(it.scheduled_for).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
              <Button onClick={scheduleCampaign} disabled={phase === 'scheduling'} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="schedule-campaign-btn">
                {phase === 'scheduling' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Rocket className="w-4 h-4 mr-2" />}
                Schedule {preview.total} {kitId ? 'branded ' : ''}posts
              </Button>
              <p className="text-[11px] text-zinc-400 text-center">
                Posts are scheduled as drafts→scheduled. {kitId ? 'Brand-framed' : 'AI'} images generate in the background.
              </p>
            </div>
          )}
        </CardContent></Card>
      </div>
    </div>
  );
};

export default BrandedCampaignBuilder;
